import { useMemo } from 'react'

import { participantInfoObserver } from '@livekit/components-core'
import type { Participant } from 'livekit-client'

import { useEnsureParticipant } from '@/src/context'

import { useObservableState } from '@/src/hooks/internal'

/**
 * The `useParticipantInfo` hook returns the identity, name, and metadata of a given participant.
 * It requires a `Participant` object passed as property or via the `ParticipantContext`.
 *
 * @example
 * ```tsx
 * const { identity, name, metadata } = useParticipantInfo({ participant });
 * ```
 * @public
 */
export interface UseParticipantInfoOptions {
  participant?: Participant
}

/** @public */
export function useParticipantInfo(props: UseParticipantInfoOptions = {}) {
  const p = useEnsureParticipant(props.participant)
  const infoObserver = useMemo(() => participantInfoObserver(p), [p])
  const { identity, name, metadata } = useObservableState(infoObserver, {
    name: p.name,
    identity: p.identity,
    metadata: p.metadata,
  })

  return { identity, name, metadata }
}
